import { useMutation, useQueryClient } from "@tanstack/react-query";
import useCreateAccountHolderNotesMutation from "./useCreateAccountHolderNotesMutation";
import {
  AccountHolder,
  updateRatePlans,
  UpdateRatePlansBody,
} from "../../../services/accountService";
import { getRatePlanChanges } from "./useUpdateRatePlanMutation";

export default function useUpdateWalletMutation(accountHolderCode: string) {
  const queryClient = useQueryClient();
  const { mutateAsync: createNote } = useCreateAccountHolderNotesMutation();

  return useMutation<string, Error, UpdateRatePlansBody>(
    (body) => updateRatePlans(body),
    {
      async onSuccess(_, updatedRatePlansReq) {
        const accountConfigData = queryClient.getQueryData<AccountHolder>([
          "accountConfig",
          accountHolderCode,
        ]);

        const existingRatePlans = [...(accountConfigData?.ratePlans || [])];
        const incomingRatePlans = (updatedRatePlansReq as any)?.ratePlans || [];

        let replacedAny = false;
        const updatedRatePlans = [...existingRatePlans];

        for (const ratePlan of incomingRatePlans) {
          if (!ratePlan?.paymentMethodType) continue;

          const idx = updatedRatePlans.findIndex(
            (rp) => rp.paymentMethodType === ratePlan.paymentMethodType
          );

          if (idx >= 0) {
            updatedRatePlans[idx] = ratePlan;
            replacedAny = true;
          } else {
            updatedRatePlans.push(ratePlan);
          }
        }

        queryClient.setQueryData(["accountConfig", accountHolderCode], {
          ...accountConfigData,
          ratePlans: updatedRatePlans,
        });

        // Create notes using getRatePlanChanges for better verbiage
        if (incomingRatePlans?.length) {
          for (const incoming of incomingRatePlans) {
            if (!incoming?.paymentMethodType) continue;

            const existing = existingRatePlans.find(
              (rp) => rp.paymentMethodType === incoming.paymentMethodType
            );

            // Added rate plan
            if (!existing) {
              await createNote({
                accountHolderCode,
                freeText: `Added rate plan (${incoming.paymentMethodType}).`,
              });
              continue;
            }

            // Updated rate plan
            const changes = getRatePlanChanges(existing, incoming);

            // Support either: string[], { changes: string[] }, or string
            let changeList: string[] = [];
            if (Array.isArray(changes)) {
              changeList = changes;
            } else if (changes && Array.isArray((changes as any).changes)) {
              changeList = (changes as any).changes;
            } else if (typeof changes === "string" && changes.trim()) {
              changeList = [changes];
            }

            if (changeList.length) {
              await createNote({
                accountHolderCode,
                freeText: `${changeList.join("; ")}`,
              });
            }
          }
        }

        queryClient.invalidateQueries(["accountConfig", accountHolderCode]);
      },
    }
  );
}
